import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
} from '@mui/joy';
import {
  Plus as AddIcon,
  PencilSimple as EditIcon,
  Gear as SettingsIcon,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import PreviewSiteButton from '../../components/cms/PreviewSiteButton';

interface Site {
  id: number;
  name: string;
  domain: string;
  theme_key: string;
  is_active: boolean;
  created_at: string;
  page_count?: number;
}

export const SiteList: React.FC = () => {
  const navigate = useNavigate();

  const { data: sites, isLoading, error } = useQuery({
    queryKey: ['cms-sites'],
    queryFn: async () => {
      console.log('SiteList: Fetching sites...');
      try {
        const response = await api.get<any>('/api/cms/sites/');
        console.log('SiteList: API Response:', response);
        // The API client returns { data: T } where data contains the actual response
        // For paginated endpoints, the data will have a 'results' property
        const sites = (response.data?.results || response.data || []) as Site[];
        console.log('SiteList: Parsed sites:', sites);
        return sites;
      } catch (error) {
        console.error('SiteList: Error fetching sites:', error);
        throw error;
      }
    },
  });

  console.log('SiteList render - isLoading:', isLoading, 'sites:', sites, 'error:', error);

  React.useEffect(() => {
    if (!isLoading && sites && sites.length === 0) {
      navigate('/cms/sites/quick-setup');
    }
  }, [isLoading, sites, navigate]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="danger">
          Error loading sites: {error instanceof Error ? error.message : 'Unknown error'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography level="h2">CMS Sites</Typography>
        {/* Organizations can only have one site */}
        {sites && sites.length > 0 ? (
          <PreviewSiteButton
            siteId={sites[0].id}
            size="sm"
            variant="outlined"
            domain={sites[0].domain}
            isActive={sites[0].is_active}
          />
        ) : (
          <Button startDecorator={<AddIcon />} onClick={() => navigate('/cms/sites/quick-setup')}>
            Create Site
          </Button>
        )}
      </Box>

      {sites?.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography level="body-md" sx={{ mb: 2 }}>
                No website created yet
              </Typography>
              <Typography level="body-sm" sx={{ mb: 3, color: 'text.tertiary' }}>
                Each organization can have one website. Create yours to get started.
              </Typography>
              <Button startDecorator={<AddIcon />} onClick={() => navigate('/cms/sites/quick-setup')}>
                Create Your Website
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
          {sites?.map((site) => (
            <Card key={site.id} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography level="title-lg">{site.name}</Typography>
                  <Chip
                    size="sm"
                    color={site.is_active ? 'success' : 'neutral'}
                    variant={site.is_active ? 'solid' : 'soft'}
                  >
                    {site.is_active ? 'Active' : 'Inactive'}
                  </Chip>
                </Box>

                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Domain: <strong>{site.domain}</strong>
                </Typography>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Theme: <strong>{site.theme_key || 'None'}</strong>
                </Typography>
                <Typography level="body-sm" sx={{ mb: 2 }}>
                  Pages: <strong>{site.page_count || 0}</strong>
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="sm"
                    variant="outlined"
                    color="primary"
                    startDecorator={<SettingsIcon />}
                    onClick={() => navigate(`/cms/sites/${site.id}/settings`)}
                  >
                    Settings
                  </Button>
                  <Button
                    size="sm"
                    startDecorator={<EditIcon />}
                    onClick={() => navigate(`/cms/sites/${site.id}/pages`)}
                  >
                    Manage Pages
                  </Button>
                  <PreviewSiteButton
                    siteId={site.id}
                    size="sm"
                    variant="outlined"
                    domain={site.domain}
                    isActive={site.is_active}
                  >
                    Preview
                  </PreviewSiteButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};
